import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, AlertTriangle, BarChart3 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const GOLD = "#C9A84C";
const fmtCents = (c) => (c == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100));

const POLICY_TYPES = [
    { value: "", label: "Any type" },
    { value: "term", label: "Term" },
    { value: "whole_life", label: "Whole Life" },
    { value: "iul", label: "IUL" },
    { value: "final_expense", label: "Final Expense" },
    { value: "annuity", label: "Annuity" },
    { value: "health", label: "Health" },
    { value: "medicare", label: "Medicare" },
    { value: "dental_vision", label: "Dental/Vision" },
];

export default function AdminPoliciesByAgent() {
    const today = new Date().toISOString().slice(0, 10);
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

    const [filters, setFilters] = useState({
        from: firstOfMonth,
        to: today,
        carrier: "",
        policy_type: "",
        agent_id: "",
    });
    const [carriers, setCarriers] = useState([]);
    const [agents, setAgents] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.carriers({ active: true }).then(setCarriers).catch(() => {});
        adminApi.team?.().then(setAgents).catch(() => {});
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        const params = {
            from: filters.from, to: filters.to,
            ...(filters.carrier ? { carrier: filters.carrier } : {}),
            ...(filters.policy_type ? { policy_type: filters.policy_type } : {}),
            ...(filters.agent_id ? { agent_id: filters.agent_id } : {}),
        };
        adminApi.reportPoliciesByAgent(params)
            .then(setData)
            .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load report."))
            .finally(() => setLoading(false));
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        const lines = ["Agent,Lead,Carrier,Policy Type,Annual Premium,Effective Date"];
        (data?.groups || []).forEach((g) => {
            (g.policies || []).forEach((p) => {
                lines.push([
                    csvCell(g.agent_name), csvCell(p.lead_name || ""),
                    csvCell(p.carrier), p.policy_type,
                    (p.annual_premium_cents || 0) / 100,
                    p.effective_date || "",
                ].join(","));
            });
            lines.push(`${csvCell(g.agent_name)} subtotal,,,,${(g.subtotal_annual_premium_cents || 0) / 100},`);
        });
        downloadBlob(lines.join("\n"), `policies_by_agent_${filters.from}_to_${filters.to}.csv`);
    };

    return (
        <div className="space-y-5" data-testid="report-policies-by-agent">
            <header>
                <div className="mm-eyebrow flex items-center gap-1.5"><BarChart3 size={11} /> Report</div>
                <h1 className="mm-display text-3xl text-mm-navy">Policies by Agent</h1>
            </header>

            <div className="mm-card flex flex-wrap items-end gap-3 p-4">
                <Field label="From">
                    <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="pba-from" />
                </Field>
                <Field label="To">
                    <input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="pba-to" />
                </Field>
                <Field label="Carrier">
                    <select value={filters.carrier} onChange={(e) => setFilters((f) => ({ ...f, carrier: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="pba-carrier">
                        <option value="">Any</option>
                        {carriers.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                </Field>
                <Field label="Policy Type">
                    <select value={filters.policy_type} onChange={(e) => setFilters((f) => ({ ...f, policy_type: e.target.value }))} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="pba-policy-type">
                        {POLICY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </Field>
                <button onClick={exportCsv} className="mm-btn-outline ml-auto !py-1.5 text-xs" data-testid="pba-csv">
                    <Download size={12} /> CSV
                </button>
            </div>

            <div className="rounded-md border border-mm-line bg-mm-bg/60 px-3 py-2 text-[11px] text-mm-muted">
                {data?.disclaimer || "This report summarizes closed policies by agent. Actual carrier compensation is determined per contract."}
            </div>

            {loading ? (
                <div className="mm-card py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
            ) : (data?.groups?.length || 0) === 0 ? (
                <div className="mm-card py-10 text-center text-mm-muted">No policies match your filters.</div>
            ) : (
                <div className="space-y-4">
                    {data.groups.map((g) => (
                        <div key={g.agent_id} className="mm-card overflow-hidden" data-testid={`pba-group-${g.agent_id}`}>
                            <header className="flex items-center justify-between bg-mm-bg/60 px-4 py-2.5">
                                <div className="font-semibold text-mm-navy">{g.agent_name}</div>
                                <div className="text-xs text-mm-muted">
                                    {g.policy_count} polic{g.policy_count === 1 ? "y" : "ies"} ·
                                    Premium <strong>{fmtCents(g.subtotal_annual_premium_cents)}</strong>
                                </div>
                            </header>
                            <table className="w-full text-sm">
                                <thead className="bg-mm-bg/30 text-[10px] uppercase tracking-wider text-mm-muted">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium">Lead</th>
                                        <th className="px-3 py-2 text-left font-medium">Carrier · Type</th>
                                        <th className="px-3 py-2 text-right font-medium">Annual Premium</th>
                                        <th className="px-3 py-2 text-left font-medium">Effective</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {g.policies.map((p) => (
                                        <tr key={p.policy_id} className="border-t border-mm-line">
                                            <td className="px-3 py-2">{p.lead_name || "—"}</td>
                                            <td className="px-3 py-2">{p.carrier} · {p.policy_type}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{fmtCents(p.annual_premium_cents)}</td>
                                            <td className="px-3 py-2 text-mm-muted">{p.effective_date || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-mm-muted">{label}</span>
            {children}
        </label>
    );
}

function csvCell(s) { if (s == null) return ""; const str = String(s); return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str; }
function downloadBlob(data, filename) {
    const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
